<template>
  <aside class="sidebar">

    <!-- ── Logo ──────────────────────────────────────────────── -->
    <div class="sidebar-brand">
      <img src="/payfix-logo.png" alt="Payfix" class="sidebar-logo" />
    </div>

    <!-- ── Aktive Firma (Company Switcher) ───────────────────── -->
    <RouterLink to="/select-company" class="company-switcher" id="btn-switch-company">
      <div v-if="companyStore.hasCompany" class="company-active">
        <div class="company-av">{{ companyStore.selectedCompany!.name.slice(0,2).toUpperCase() }}</div>
        <div class="company-info">
          <p class="company-name">{{ companyStore.selectedCompany!.name }}</p>
          <p class="company-hint">{{ $t('nav.switchCompany') }}</p>
        </div>
        <svg class="switcher-arrow" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 16V4m0 0L3 8m4-4l4 4"/><path d="M17 8v12m0 0l4-4m-4 4l-4-4"/></svg>
      </div>
      <div v-else class="company-none">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        <span>{{ $t('nav.selectCompany') }}</span>
        <svg class="switcher-arrow" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
      </div>
    </RouterLink>

    <!-- ── Navigation ────────────────────────────────────────── -->
    <nav class="sidebar-nav" :aria-label="$t('nav.settings')">

      <!-- Hauptbereich (nur mit Firma-Kontext) -->
      <div class="nav-group" :class="{ 'nav-group--disabled': !companyStore.hasCompany }">
        <!-- Die Uebersichtsseite, an ERSTER Stelle (T-M41.3, REQ-OPS-035
             Praez. 1 (a), OP-83). Sie war seit Bestehen des Routers gebaut und
             von keiner Stelle der Anwendung aus erreichbar; ihre fuenf
             Kennzahl-Kacheln zaehlen seit T-M10.3 serverseitig (REQ-OPS-034).

             Die Beschriftung haengt an einem EIGENEN Schluessel `nav.overview`
             und nicht am vorhandenen `nav.dashboard`: jener beschriftet zwei
             andere Dinge — die Herkunft eines von Hand angelegten Auftrags
             (`JobCard.vue`) und den Rueckweg in die Anwendung
             (`AuthActionView.vue`). Ein gemeinsamer Schluessel haette eine
             spaetere Umbenennung dieses Menuepunktes still in beide
             mitgezogen. -->
        <NavItem to="/dashboard" icon="dashboard" :label="$t('nav.overview')" id-suffix="dashboard" :badge="null" />
        <!-- REQ-OPS-034: die Zahl wartender Auftraege kommt aus der
             serverseitigen Zaehlung, nicht aus dem 50er-Live-Fenster der
             Auftragsliste. Liegt noch keine Zaehlung vor, wird sie als
             Untergrenze gekennzeichnet („≥ n") statt als exakte Zahl. -->
        <NavItem
          to="/jobs" icon="print" :label="$t('nav.jobs')" id-suffix="jobs"
          :badge="pendingBadgeCount || null"
          :badge-exact="pendingBadgeExact"
          :badge-title="pendingBadgeExact ? undefined : $t('common.count.atLeastHint')"
        />
        <NavItem to="/fleet"        icon="server"       :label="$t('nav.fleet')"        id-suffix="fleet"        :badge="null" />
        <NavItem to="/printers"     icon="printer"      :label="$t('nav.printers')"     id-suffix="printers"     :badge="null" />
        <NavItem to="/schankanlage"  icon="schankanlage" :label="$t('nav.schankanlage')"   id-suffix="schankanlage" :badge="null" />
        <NavItem to="/printserver"   icon="server"       :label="$t('nav.printserver')"  id-suffix="printserver"  :badge="null" />
      </div>

      <!-- Einstellungen
           ⚠ DREI EINTRAEGE SIND ADMIN-ONLY (REQ-SEC-027 (c), T-M30.7):
           Benutzerverwaltung, SMTP/Twilio (`settings`) und API-Tokens. Die
           Aufzaehlung ist ABSCHLIESSEND — Firma, STANDORTE und Druckserver
           bleiben fuer die Rolle `user` sichtbar und bedienbar, die Standorte
           ausdruecklich samt `updateChannel` und `pinnedVersion` (Entscheid des
           Auftraggebers vom 2026-09-01 zu OP-185). Das Verbergen ist
           Bequemlichkeit; gesperrt wird in den Rules. -->
      <div class="nav-group">
        <p class="nav-section-label">{{ $t('nav.settings') }}</p>
        <NavItem v-if="authStore.isAdmin" to="/settings/users" icon="users" :label="$t('nav.users')" id-suffix="settings-users" :badge="null" :indent="true" />
        <NavItem to="/settings/company"     icon="company" :label="$t('nav.company')"     id-suffix="settings-company"     :badge="null" :indent="true" />
        <NavItem to="/settings/locations"   icon="location" :label="$t('nav.locations')"  id-suffix="settings-locations"   :badge="null" :indent="true" />
        <NavItem to="/settings/printserver" icon="gear"    :label="$t('nav.printserver')" id-suffix="settings-printserver" :badge="null" :indent="true" />
        <NavItem v-if="authStore.isAdmin" to="/settings/smtp" icon="mail" :label="$t('nav.smtp')" id-suffix="settings-smtp" :badge="null" :indent="true" />
        <NavItem v-if="authStore.isAdmin" to="/settings/api"  icon="code" :label="$t('nav.api')"  id-suffix="settings-api"  :badge="null" :indent="true" />
      </div>

    </nav>

    <!-- ── Sprach-Switcher (Dropdown) ───────────────────────── -->
    <div class="lang-switcher" ref="langRef">
      <button class="lang-trigger" @click="langOpen = !langOpen" id="btn-lang-trigger">
        <span class="lang-flag-lg">{{ currentLocaleObj?.flag }}</span>
        <span class="lang-label">{{ currentLocaleObj?.label }}</span>
        <svg class="lang-chevron" :class="{ 'lang-chevron--open': langOpen }"
          width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      <Transition name="dropdown">
        <div v-if="langOpen" class="lang-dropdown">
          <button
            v-for="loc in SUPPORTED_LOCALES"
            :key="loc.code"
            class="lang-option"
            :class="{ 'lang-option--active': currentLocale === loc.code }"
            @click="changeLocale(loc.code)"
            :id="`btn-lang-${loc.code}`"
          >
            <span class="lang-flag-lg">{{ loc.flag }}</span>
            <span class="lang-option-label">{{ loc.label }}</span>
            <svg v-if="currentLocale === loc.code" class="lang-check"
              width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </button>
        </div>
      </Transition>
    </div>

    <!-- ── User / Logout ──────────────────────────────────────── -->
    <div class="sidebar-footer">
      <div class="user-block">
        <div class="user-avatar">{{ userInitials }}</div>
        <div class="user-info">
          <p class="user-name">{{ displayName }}</p>
          <p class="user-role">{{ roleLabel }}</p>
        </div>
      </div>
      <button class="logout-btn" @click="handleLogout" :title="$t('nav.logout')" id="btn-logout">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
          <polyline points="16 17 21 12 16 7"/>
          <line x1="21" y1="12" x2="9" y2="12"/>
        </svg>
      </button>
    </div>

  </aside>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useRouter }                   from 'vue-router';
import { useI18n }                     from 'vue-i18n';
import { useAuthStore }                from '@/stores/authStore';
import { useCompanyStore }             from '@/stores/companyStore';
import { usePrintJobStore }            from '@/stores/printJobStore';
import NavItem                         from '@/components/NavItem.vue';
import { SUPPORTED_LOCALES, setLocale, type SupportedLocale } from '@/i18n';

const authStore    = useAuthStore();
const companyStore = useCompanyStore();
const jobStore     = usePrintJobStore();
const router       = useRouter();
const { t, locale } = useI18n();

// ── Abzeichen wartender Auftraege (REQ-OPS-034, REQ-LC-015) ─────────────────
// Serverseitig gezaehlt. Solange keine Zaehlung vorliegt, ist die Zahl aus dem
// Live-Fenster nur eine UNTERGRENZE — sie wird als solche gekennzeichnet und
// nicht als exakte Zahl gezeigt.
//
// HIER GILT DIE ENGERE FORM (REQ-LC-015 Praez. 1 (b)): der zurueckgehaltene
// Auftrag zaehlt NICHT mit, und er wird hier auch nicht daneben ausgewiesen.
// Ein Abzeichen ist eine einzige Zahl UND ein Dringlichkeitssignal; es hat
// keinen Platz fuer eine zweite Menge, und an einem Standort mit
// Vorbestellungen leuchtete es dauerhaft. Genau daran ginge der echte Stau
// unter (REQ-PS-021). Die benannte Menge steht auf der Uebersicht
// (`kpi-held`), wo Platz dafuer ist.
//
// `countedStats.pending` ist seit T-M15.6 bereits die WARTENDE Menge — der
// Abzug geschieht an genau einer Stelle (`subtractHeld`), nicht hier.
const pendingBadgeCount = computed(() => jobStore.countedStats.pending.value);
const pendingBadgeExact = computed(() => jobStore.countedStats.pending.exact);

// ── Language Dropdown ─────────────────────────────────────────
const langOpen = ref(false);
const langRef  = ref<HTMLElement | null>(null);

const currentLocale    = computed(() => locale.value as SupportedLocale);
const currentLocaleObj = computed(() => SUPPORTED_LOCALES.find(l => l.code === currentLocale.value));

function changeLocale(code: SupportedLocale) {
  setLocale(code);
  langOpen.value = false;
}

function onClickOutside(e: MouseEvent) {
  if (langRef.value && !langRef.value.contains(e.target as Node)) {
    langOpen.value = false;
  }
}
onMounted(() => document.addEventListener('click', onClickOutside));
onUnmounted(() => document.removeEventListener('click', onClickOutside));

const displayName = computed(() =>
  authStore.appUser?.displayName ?? authStore.firebaseUser?.email ?? t('common.unknown'),
);
const userInitials = computed(() => displayName.value.slice(0, 2).toUpperCase());
/**
 * Die Rollen-Beschriftung — ZWEI Rollen, und sie kommt aus dem ANSPRUCH
 * (T-M30.7, REQ-DM-070 (a)/(b), REQ-SEC-028 (a)).
 *
 * *Bis zum 2026-09-01 stand hier eine dritte Zeile `operator: t('role.operator')`
 * und gelesen wurde `authStore.appUser?.role`. Beides ist fort: `operator` ist
 * abgeraeumt (REQ-DM-070 (b)), und die Rolle steht im Claim und nicht im Feld.
 * Berichtigt statt geloescht — REQ-OPS-036 Praez. 6.*
 */
const roleLabel = computed(() =>
  authStore.isAdmin ? t('role.admin') : t('role.user'),
);

async function handleLogout() {
  await authStore.logout();
  router.push('/login');
}
</script>

<style scoped>
/* ── Shell ──────────────────────────────────────────────────── */
.sidebar {
  position:       fixed;
  top: 0; left: 0;
  width:          var(--sidebar-w);
  height:         100vh;
  background:     #fff;
  border-right:   1px solid var(--color-border);
  box-shadow:     2px 0 20px rgba(0,0,0,0.04);
  display:        flex;
  flex-direction: column;
  z-index:        100;
  overflow-y:     auto;
  overflow-x:     hidden;
}

/* ── Logo ───────────────────────────────────────────────────── */
.sidebar-brand {
  padding:       16px 14px 14px;
  border-bottom: 1px solid var(--color-border);
  flex-shrink:   0;
}
.sidebar-logo { height: 32px; width: auto; display: block; object-fit: contain; }

/* ── Company Switcher ───────────────────────────────────────── */
.company-switcher {
  display:         flex;
  text-decoration: none;
  padding:         10px 12px;
  border-bottom:   1px solid var(--color-border);
  transition:      background var(--t-fast);
  flex-shrink:     0;
}
.company-switcher:hover { background: var(--color-surface-2); }

.company-active {
  display:     flex;
  align-items: center;
  gap:         9px;
  width:       100%;
}
.company-av {
  width:           32px; height: 32px;
  border-radius:   var(--radius-sm);
  background:      var(--gradient-brand);
  display:         flex;
  align-items:     center;
  justify-content: center;
  font-size:       0.68rem;
  font-weight:     800;
  color:           #fff;
  flex-shrink:     0;
}
.company-info { flex: 1; min-width: 0; }
.company-name {
  font-size:     0.82rem;
  font-weight:   700;
  color:         var(--text-primary);
  white-space:   nowrap;
  overflow:      hidden;
  text-overflow: ellipsis;
  line-height:   1.2;
}
.company-hint { font-size: 0.68rem; color: var(--text-muted); }

.company-none {
  display:     flex;
  align-items: center;
  gap:         8px;
  width:       100%;
  font-size:   0.82rem;
  color:       var(--color-primary);
  font-weight: 600;
}
.company-none span { flex: 1; }

.switcher-arrow { color: var(--text-muted); flex-shrink: 0; }

/* ── Nav ────────────────────────────────────────────────────── */
.sidebar-nav  { padding: 8px 0; flex: 1; }
.nav-group    { padding: 4px 10px 8px; border-bottom: 1px solid var(--color-border); }
.nav-group:last-child { border-bottom: none; }

/* Disabled group when no company selected */
.nav-group--disabled { opacity: 0.4; pointer-events: none; }

.nav-section-label {
  font-size:      0.66rem;
  font-weight:    700;
  color:          var(--text-muted);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding:        8px 8px 4px;
}

/* ── Language Switcher Dropdown ───────────────────────────── */
.lang-switcher {
  position:     relative;
  padding:      8px 10px;
  border-top:   1px solid var(--color-border);
  flex-shrink:  0;
}
.lang-trigger {
  display:         flex;
  align-items:     center;
  gap:             8px;
  width:           100%;
  padding:         7px 10px;
  background:      var(--color-surface-2);
  border:          1px solid var(--color-border);
  border-radius:   var(--radius-md);
  cursor:          pointer;
  font-family:     var(--font-sans);
  transition:      background var(--t-fast), border-color var(--t-fast);
}
.lang-trigger:hover { background: var(--color-surface-3); border-color: var(--color-primary); }
.lang-flag-lg  { font-size: 1.1rem; line-height: 1; flex-shrink: 0; }
.lang-label    { font-size: 0.8rem; font-weight: 600; color: var(--text-primary); flex: 1; text-align: left; }
.lang-chevron  { color: var(--text-muted); flex-shrink: 0; transition: transform 0.2s; }
.lang-chevron--open { transform: rotate(180deg); }

.lang-dropdown {
  position:      absolute;
  bottom:        calc(100% - 4px);
  left:          10px;
  right:         10px;
  background:    #fff;
  border:        1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow:    0 8px 32px rgba(0,0,0,0.12);
  overflow:      hidden;
  z-index:       200;
}
.lang-option {
  display:         flex;
  align-items:     center;
  gap:             10px;
  width:           100%;
  padding:         9px 12px;
  background:      transparent;
  border:          none;
  cursor:          pointer;
  font-family:     var(--font-sans);
  transition:      background var(--t-fast);
  border-bottom:   1px solid var(--color-border);
}
.lang-option:last-child { border-bottom: none; }
.lang-option:hover { background: var(--color-surface-2); }
.lang-option--active { background: var(--color-primary-dim); }
.lang-option-label { font-size: 0.82rem; font-weight: 600; color: var(--text-primary); flex: 1; text-align: left; }
.lang-check    { color: var(--color-primary); flex-shrink: 0; }

/* Dropdown animation */
.dropdown-enter-active, .dropdown-leave-active { transition: all 0.18s ease; }
.dropdown-enter-from, .dropdown-leave-to { opacity: 0; transform: translateY(6px); }

/* ── Footer ─────────────────────────────────────────────────── */
.sidebar-footer {
  display:     flex;
  align-items: center;
  gap:         8px;
  padding:     10px 12px 14px;
  border-top:  1px solid var(--color-border);
  flex-shrink: 0;
}
.user-block {
  display:     flex;
  align-items: center;
  gap:         9px;
  flex:        1;
  min-width:   0;
}
.user-avatar {
  width:           32px; height: 32px;
  border-radius:   50%;
  background:      var(--gradient-brand);
  display:         flex;
  align-items:     center;
  justify-content: center;
  font-size:       0.7rem;
  font-weight:     700;
  color:           #fff;
  flex-shrink:     0;
}
.user-info { min-width: 0; }
.user-name { font-size: 0.8rem; font-weight: 600; color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.user-role { font-size: 0.68rem; color: var(--text-muted); }
.logout-btn {
  background:    transparent;
  border:        none;
  cursor:        pointer;
  color:         var(--text-muted);
  padding:       6px;
  border-radius: var(--radius-sm);
  transition:    color var(--t-fast), background var(--t-fast);
  display:       flex;
  align-items:   center;
  flex-shrink:   0;
}
.logout-btn:hover { color: #EF4444; background: rgba(239,68,68,0.07); }
</style>
